import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, resolveComponent as _resolveComponent, Fragment as _Fragment, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "min-w-0 px-6 pb-8 pt-0 print:!px-0 print:!py-0" }
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-4" }
const _hoisted_3 = {
  key: 0,
  class: "flex items-center gap-3 print:hidden"
}
const _hoisted_4 = { class: "print:hidden" }
const _hoisted_5 = {
  key: 0,
  class: "ml-1.5 text-xs"
}
const _hoisted_6 = { class: "font-medium" }
const _hoisted_7 = { class: "text-xs text-gray-500 [overflow-wrap:anywhere]" }
const _hoisted_8 = { class: "text-xs text-gray-500" }
const _hoisted_9 = {
  key: 4,
  class: "mt-5 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-error"
}
const _hoisted_10 = { class: "mt-2.5" }
const _hoisted_11 = {
  key: 0,
  class: "mt-5 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs",
  "data-cy": "conflict-truncated"
}
const _hoisted_12 = {
  key: 1,
  class: "mt-5 rounded border border-green-200 bg-green-50 px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-clean"
}
const _hoisted_13 = {
  key: 0,
  class: "mt-2.5"
}
const _hoisted_14 = {
  key: 2,
  class: "mt-5 rounded border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-all-filtered"
}
const _hoisted_15 = { class: "mt-2.5" }
const _hoisted_16 = { class: "mt-6 max-w-[76ch] print:hidden" }
const _hoisted_17 = {
  key: 0,
  class: "mt-3 rounded border border-l-4 border-[rgba(var(--v-theme-error),0.3)] bg-[rgba(var(--v-theme-error),0.08)] px-3.5 py-3 text-xs leading-relaxed",
  "data-cy": "conflict-save-error"
}
const _hoisted_18 = { class: "mt-3 flex justify-end" }
const _hoisted_19 = { class: "print-only print:!mt-4" }
const _hoisted_20 = {
  key: 0,
  class: "whitespace-pre-line text-sm [overflow-wrap:anywhere]"
}
const _hoisted_21 = {
  key: 1,
  class: "text-sm italic"
}

import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import RouteService from '@/services/RouteService';
import EventTracker from '@/services/eventTracker';
import {
  TrackingAction,
  TrackingCategory,
} from '@/constants/trackingConstants';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTabs from '@/components/LlTabs.vue';
import type { TabItem } from '@/components/LlTabs.vue';
import ConflictCheckLog from './components/ConflictCheckLog.vue';
import ConflictNotesField from './components/ConflictNotesField.vue';
import ConflictPrintMasthead from './components/ConflictPrintMasthead.vue';
import SavedConflictCheckView from './components/SavedConflictCheckView.vue';
import ConflictQueryPanel from './components/ConflictQueryPanel.vue';
import ConflictResultList from './components/ConflictResultList.vue';
import ConflictFieldFilter from './components/ConflictFieldFilter.vue';
import ConflictPreviewPanel from './components/ConflictPreviewPanel.vue';
import ConflictUpgradeNotice from './components/ConflictUpgradeNotice.vue';
import { useConflictCheckStore } from './stores/useConflictCheckStore';
import { useConflictCheckRecordStore } from './stores/useConflictCheckRecordStore';
import { useConflictParties } from './composables/useConflictParties';
import { CONFLICT_SCOPE_SENTENCE } from './constants/conflictFieldConstants';
import {
  CONFLICT_NOTES_MAX_LENGTH,
  CONFLICT_TAB_NEW,
  CONFLICT_TAB_PREVIOUS,
} from './constants/conflictRecordConstants';
import type {
  ConflictPrintSheet,
  ConflictResultRow,
} from './models/conflictTypes';

type StateChangeEvent = { preventDefault: () => void };
type RouteTarget = { name?: string };

const PREVIEW_COLUMN = '520px';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConflictCheckPage',
  props: {
    state: {},
    stateParams: {},
    rootScope: {},
    tracking: {}
  },
  setup(__props: any) {

const props = __props;

const store = useConflictCheckStore();
const recordStore = useConflictCheckRecordStore();
const meStore = useMeStore();
const { me } = storeToRefs(meStore);
const { handleError, handleSuccess } = useSnackbarHandler();
const routeService = new RouteService(props);

const { query } = storeToRefs(store);
const { parties, partyCount, removeParty } = useConflictParties(query);

/** Reopened by "Refine search"; the query panel is otherwise hidden after a run. */
const queryPanelOpen = ref(!store.hasRun);
const resultList = ref<InstanceType<typeof ConflictResultList> | null>(null);
const savedView = ref<InstanceType<typeof SavedConflictCheckView> | null>(null);
const leaveDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);

const uiState = reactive({ savedNotesDirty: false });

const savedCheckId = computed(() => {
  const id = props.stateParams?.check;
  return typeof id === 'string' && id ? id : null;
});

const view = computed<'new' | 'previous' | 'saved'>(() => {
  if (savedCheckId.value) return 'saved';
  return props.stateParams?.tab === CONFLICT_TAB_PREVIOUS ? 'previous' : 'new';
});

const canActOnResults = computed(
  () => store.hasRun && !store.running && !store.failed,
);
const notesOverLimit = computed(
  () => store.notes.length > CONFLICT_NOTES_MAX_LENGTH,
);

/** Only the unfiltered total is the firm's count; a filtered one would mislead. */
const previousCount = computed(() =>
  recordStore.logLoaded && !recordStore.isFiltered ? recordStore.total : null,
);

const showQueryPanel = computed(() => queryPanelOpen.value || !store.hasRun);
const showPreview = computed(
  () =>
    Boolean(store.preview) ||
    store.previewLoading ||
    Boolean(store.previewError),
);

const gridColumns = computed(() =>
  showPreview.value ? `minmax(0, 1fr) ${PREVIEW_COLUMN}` : 'minmax(0, 1fr)',
);

/**
 * An unsaved sheet still names who ran it and when: firms that print and file the
 * paper outside LeanLaw rely on the sheet alone.
 */
const unsavedSheet = computed<ConflictPrintSheet>(() => ({
  firmName: me.value?.company?.name ?? '',
  runByName: me.value?.name ?? '',
  runAt: store.ranAt,
  saved: false,
  queryText: store.ranQuery,
  parties: store.searchableParties.map((party) => party.text),
  includedTimeEntries: store.ranWithTimeEntries,
  hiddenRowCount: store.hiddenRowCount,
}));
const ignoredRoleWords = computed(() => {
  const seen: Record<string, true> = {};
  const words: string[] = [];
  (store.result?.parties ?? []).forEach((party) => {
    party.ignoredRoleWords.forEach((word) => {
      if (!seen[word]) {
        seen[word] = true;
        words.push(word);
      }
    });
  });
  return words;
});

/**
 * Set when the next unmount is a move within the check or out to one of its records,
 * so what the reviewer has on screen is still here when they come back (EC-003).
 */
let keepState = false;
/** Set once the reviewer has agreed to lose their notes, so the retry is not stopped. */
let leaveConfirmed = false;
let stopWatchingRoutes: (() => void) | null = null;

/** Result records open under clients (matters are nested there); going there is review. */
const isRecordRoute = (name: string): boolean => name.startsWith('clients.');

function onStateChangeStart(
  event: StateChangeEvent,
  toState: RouteTarget,
  toParams: Record<string, unknown>,
): void {
  const name = toState?.name ?? '';
  const staysInCheck = name === 'conflict' || isRecordRoute(name);
  // Notes typed into a saved check live only in its view, which a move remounts.
  const wouldLoseNotes =
    uiState.savedNotesDirty || (store.hasUnsavedNotes && !staysInCheck);
  if (wouldLoseNotes && !leaveConfirmed) {
    event.preventDefault();
    confirmLeave(name, toParams);
    return;
  }
  // Confirming only gives up the notes the dialog warned about; a move within the
  // check still keeps the new-check draft.
  keepState = staysInCheck;
}

async function confirmLeave(
  name: string,
  params: Record<string, unknown>,
): Promise<void> {
  try {
    const leave = await leaveDialog.value?.open();
    if (!leave) return;
    leaveConfirmed = true;
    uiState.savedNotesDirty = false;
    routeService.go(name, params);
  } catch (error) {
    handleError(error);
  }
}

function onBeforeUnload(event: BeforeUnloadEvent): void {
  if (!store.hasUnsavedNotes && !uiState.savedNotesDirty) return;
  event.preventDefault();
  // Chrome still requires returnValue to show the prompt.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
}

async function loadSavedCheck(): Promise<void> {
  if (!savedCheckId.value) return;
  try {
    await recordStore.loadRecord(savedCheckId.value);
  } catch (error) {
    // A missing check has its own state (ERR-010); only other failures need a reason.
    if (!recordStore.recordNotFound) handleError(error);
  }
}

async function loadPreviousCount(): Promise<void> {
  try {
    await recordStore.loadLog();
  } catch {
    // The count is a nicety on the tab; the log itself reports its own failure.
  }
}

onMounted(async () => {
  const rootScope = props.rootScope as
    | { $on?: (name: string, fn: typeof onStateChangeStart) => () => void }
    | undefined;
  if (typeof rootScope?.$on === 'function') {
    stopWatchingRoutes = rootScope.$on('$stateChangeStart', onStateChangeStart);
  }
  window.addEventListener('beforeprint', expandPartialMatches);
  window.addEventListener('beforeunload', onBeforeUnload);
  if (view.value === 'saved') loadSavedCheck();
  if (view.value === 'new') loadPreviousCount();
  try {
    await meStore.getMe();
    store.setBillingInstructionsAccess(
      Boolean(me.value?.permissions?.matters?.billingInstructions),
    );
  } catch (error) {
    handleError(error);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeprint', expandPartialMatches);
  window.removeEventListener('beforeunload', onBeforeUnload);
  if (stopWatchingRoutes) stopWatchingRoutes();
  store.closePreview();
  // Results and notes hold party names and matter content; they go with the page
  // unless the reviewer is only stepping out to a record or to another tab.
  if (!keepState) {
    store.reset();
    recordStore.reset();
  }
});

/** A collapsed panel prints as nothing, so partial matches would silently vanish. */
function expandPartialMatches(): void {
  if (resultList.value) resultList.value.expandPartials();
  if (savedView.value) savedView.value.expandPartials();
}

async function run(): Promise<void> {
  queryPanelOpen.value = false;
  try {
    await store.runCheck();
  } catch (error) {
    // The page renders its own error state; the toast is for the reason why.
    handleError(error);
  }
}

function refine(): void {
  queryPanelOpen.value = true;
}

function onRemoveParty(index: number): void {
  store.query = removeParty(index);
  queryPanelOpen.value = true;
}

async function rerunWithTimeEntries(): Promise<void> {
  store.includeTimeEntries = true;
  try {
    await run();
  } catch (error) {
    handleError(error);
  }
}

function print(): void {
  expandPartialMatches();
  trackPrinted();
  window.print();
}

// EV-006: frontend-emitted because printing never reaches the server.
function trackPrinted(): void {
  if (!props.tracking) return;
  const checkId = view.value === 'saved' ? recordStore.record?.id : null;
  EventTracker.trackEvent(
    props.tracking,
    TrackingCategory.CONFLICT_CHECK,
    TrackingAction.PRINTED,
    checkId ? { checkId } : {},
  );
}

const conflictTabs: TabItem[] = [
  { value: CONFLICT_TAB_NEW, label: 'New check', dataCy: 'conflict-tab-new' },
  { value: CONFLICT_TAB_PREVIOUS, label: 'Previous checks', dataCy: 'conflict-tab-previous' },
];

function openTab(tab: string): void {
  routeService.go('conflict', {
    tab: tab === CONFLICT_TAB_PREVIOUS ? CONFLICT_TAB_PREVIOUS : null,
    check: null,
  });
}

function openSavedCheck(id: string): void {
  routeService.go('conflict', { tab: null, check: id });
}

const checkHref = (id: string): string | null =>
  routeService.href('conflict', { tab: null, check: id }, { absolute: false });

function openSavedPreview(row: ConflictResultRow): void {
  store.openPreview(row, recordStore.allRows);
}

async function saveCheck(): Promise<void> {
  if (notesOverLimit.value) return;
  try {
    const saved = await store.save();
    recordStore.setRecord(saved);
    store.reset();
    handleSuccess('Conflict check saved.');
    openSavedCheck(saved.id);
  } catch (error) {
    // ERR-008: the results and notes stay on screen; the banner says it did not save.
    handleError(error);
  }
}

/**
 * Opening a record leaves the check behind, and a reviewer clearing seven parties
 * does not want to lose the results to look one of them up — so these are links to
 * a new tab, built by ui-router rather than assembled by hand.
 */
const previewClientHref = computed(() => {
  const preview = store.preview;
  if (!preview) return null;
  const clientId =
    preview.type === 'client' ? preview.id : preview.client?.id ?? null;
  // `clients.client` redirects to the overview; a reviewer opening a conflict
  // result wants the record itself, which is the info tab.
  return clientId
    ? routeService.href('clients.client.view.info', { id: clientId })
    : null;
});

const previewMatterHref = computed(() => {
  const preview = store.preview;
  if (!preview || preview.type !== 'matter') return null;
  const clientId = preview.client?.id;
  // The matter route is nested under its client, so both ids are needed.
  return clientId
    ? routeService.href('clients.client.matter', {
        id: clientId,
        matterId: preview.id,
      })
    : null;
});

// A new query invalidates the run that produced the results on screen.
watch(
  () => store.query,
  (next) => {
    if (store.hasRun && next !== store.ranQuery) queryPanelOpen.value = true;
  },
);

return (_ctx: any,_cache: any) => {
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!

  return (_openBlock(), _createElementBlock("div", {
    class: "grid",
    style: _normalizeStyle({ gridTemplateColumns: gridColumns.value })
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(ConflictUpgradeNotice),
      _createElementVNode("div", _hoisted_2, [
        _cache[10] || (_cache[10] = _createElementVNode("h1", { class: "mt-0 text-2xl font-normal" }, "Conflict check", -1)),
        (view.value === 'new' && canActOnResults.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _cache[9] || (_cache[9] = _createElementVNode("span", {
                class: "text-xs text-gray-500",
                "data-cy": "conflict-unsaved-indicator"
              }, " Not saved ", -1)),
              _createVNode(LlSecondaryButton, {
                "data-cy": "conflict-print",
                onClick: print
              }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode(" Print ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(LlSecondaryButton, {
                color: "primary",
                loading: _unref(store).saving,
                disabled: notesOverLimit.value,
                "data-cy": "conflict-save",
                onClick: saveCheck
              }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode(" Save check ", -1)
                ]))]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createVNode(LlTabs, {
          "model-value": 
            view.value === 'new' ? _unref(CONFLICT_TAB_NEW) : _unref(CONFLICT_TAB_PREVIOUS)
          ,
          items: conflictTabs,
          class: "mb-2",
          "data-cy": "conflict-tabs",
          onSelect: _cache[0] || (_cache[0] = (tab) => openTab(String(tab)))
        }, {
          label: _withCtx(({ item }) => [
            _createTextVNode(_toDisplayString(item.label) + " ", 1),
            (item.value === _unref(CONFLICT_TAB_PREVIOUS) && previousCount.value !== null)
              ? (_openBlock(), _createElementBlock("span", _hoisted_5, " (" + _toDisplayString(previousCount.value) + ") ", 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["model-value"])
      ]),
      (view.value === 'saved')
        ? (_openBlock(), _createBlock(SavedConflictCheckView, {
            key: 0,
            ref_key: "savedView",
            ref: savedView,
            onBack: _cache[1] || (_cache[1] = ($event: any) => (openTab(_unref(CONFLICT_TAB_PREVIOUS)))),
            onRetry: loadSavedCheck,
            onPrint: print,
            onOpen: openSavedPreview,
            onDirty: _cache[2] || (_cache[2] = ($event: any) => (uiState.savedNotesDirty = $event))
          }, null, 512))
        : (view.value === 'previous')
          ? (_openBlock(), _createBlock(ConflictCheckLog, {
              key: 1,
              "check-href": checkHref,
              onOpen: openSavedCheck,
              onNew: _cache[3] || (_cache[3] = ($event: any) => (openTab(_unref(CONFLICT_TAB_NEW))))
            }))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _cache[22] || (_cache[22] = _createElementVNode("p", { class: "mb-4 mt-1 max-w-[64ch] text-sm text-gray-500 print:hidden" }, " Enter every party involved. Results name the field that matched, the word that matched it, and the name it came from. ", -1)),
              (_unref(store).hasRun)
                ? (_openBlock(), _createBlock(ConflictPrintMasthead, {
                    key: 0,
                    sheet: unsavedSheet.value
                  }, null, 8, ["sheet"]))
                : _createCommentVNode("", true),
              (showQueryPanel.value)
                ? (_openBlock(), _createBlock(ConflictQueryPanel, {
                    key: 1,
                    modelValue: _unref(store).query,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(store).query) = $event)),
                    "include-time-entries": _unref(store).includeTimeEntries,
                    "onUpdate:includeTimeEntries": _cache[5] || (_cache[5] = ($event: any) => ((_unref(store).includeTimeEntries) = $event)),
                    class: "print:hidden",
                    parties: _unref(parties),
                    "party-count": _unref(partyCount),
                    running: _unref(store).running,
                    "skipped-parties": _unref(store).result?.skippedParties ?? [],
                    "ignored-role-words": ignoredRoleWords.value,
                    onRemoveParty: onRemoveParty,
                    onRun: run
                  }, null, 8, ["modelValue", "include-time-entries", "parties", "party-count", "running", "skipped-parties", "ignored-role-words"]))
                : (_openBlock(), _createBlock(_component_v_card, {
                    key: 2,
                    class: "flex flex-wrap items-center gap-3.5 border border-gray-200 px-4 py-3",
                    elevation: "0",
                    "data-cy": "conflict-summary-strip"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(store).searchableParties.length) + " " + _toDisplayString(_unref(store).searchableParties.length === 1 ? 'name' : 'names') + " checked ", 1),
                      _createElementVNode("span", _hoisted_7, _toDisplayString(_unref(store).searchableParties.map((p) => p.text).join(' · ')), 1),
                      _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(store).ranWithTimeEntries
                ? 'Time entries included'
                : 'Time entries not searched'), 1),
                      _createVNode(LlSecondaryButton, {
                        class: "ml-auto print:hidden",
                        size: "small",
                        "data-cy": "conflict-refine",
                        onClick: refine
                      }, {
                        default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                          _createTextVNode(" Refine search ", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })),
              (_unref(store).running)
                ? (_openBlock(), _createBlock(_component_v_skeleton_loader, {
                    key: 3,
                    class: "mt-5",
                    type: "list-item-three-line@4",
                    "data-cy": "conflict-running"
                  }))
                : (_unref(store).failed)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _cache[13] || (_cache[13] = _createElementVNode("span", { class: "font-medium text-red-700" }, "The conflict check did not run.", -1)),
                      _cache[14] || (_cache[14] = _createTextVNode(" Nothing was searched, so nothing here says these names are clear. ", -1)),
                      _createElementVNode("div", _hoisted_10, [
                        _createVNode(LlSecondaryButton, {
                          size: "small",
                          "data-cy": "conflict-retry",
                          onClick: run
                        }, {
                          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                            _createTextVNode(" Try again ", -1)
                          ]))]),
                          _: 1
                        })
                      ])
                    ]))
                  : (_unref(store).hasRun)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                        (_unref(store).result?.truncated)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, " Only the first " + _toDisplayString(_unref(store).result.partiesChecked) + " of " + _toDisplayString(_unref(store).result.partiesFound) + " names were checked. Remove some names and run again for the rest. ", 1))
                          : _createCommentVNode("", true),
                        (!_unref(store).allRows.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              _cache[16] || (_cache[16] = _createElementVNode("span", { class: "font-medium text-green-800" }, "No conflicts found.", -1)),
                              _createTextVNode(" Searched " + _toDisplayString(_unref(CONFLICT_SCOPE_SENTENCE)) + _toDisplayString(_unref(store).ranWithTimeEntries
                ? ', and time entry descriptions.'
                : '. Time entries were not searched.') + " Invoice narratives are not searched. ", 1),
                              (!_unref(store).ranWithTimeEntries)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _createVNode(LlSecondaryButton, {
                                      size: "small",
                                      "data-cy": "conflict-rerun-with-entries",
                                      onClick: rerunWithTimeEntries
                                    }, {
                                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                        _createTextVNode(" Run again including time entries ", -1)
                                      ]))]),
                                      _: 1
                                    })
                                  ]))
                                : _createCommentVNode("", true)
                            ]))
                          : (!_unref(store).visibleRowCount)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _cache[18] || (_cache[18] = _createElementVNode("span", { class: "font-medium" }, "Every result is hidden by the field filter.", -1)),
                                _createTextVNode(" This check returned " + _toDisplayString(_unref(store).allRows.length) + " " + _toDisplayString(_unref(store).allRows.length === 1 ? 'result' : 'results') + ". None of them are in the fields you have left selected. ", 1),
                                _createElementVNode("div", _hoisted_15, [
                                  _createVNode(LlSecondaryButton, {
                                    size: "small",
                                    onClick: _unref(store).showAllFields
                                  }, {
                                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                      _createTextVNode(" Show all fields ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["onClick"])
                                ])
                              ]))
                            : (_openBlock(), _createBlock(ConflictResultList, {
                                key: 3,
                                ref_key: "resultList",
                                ref: resultList,
                                "full-matches": _unref(store).visibleFullMatches,
                                "partial-matches": _unref(store).visiblePartialMatches,
                                onOpen: _unref(store).openPreview
                              }, null, 8, ["full-matches", "partial-matches", "onOpen"])),
                        _createElementVNode("div", _hoisted_16, [
                          _createVNode(ConflictNotesField, {
                            id: "conflict-notes-input",
                            modelValue: _unref(store).notes,
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(store).notes) = $event)),
                            disabled: _unref(store).saving
                          }, null, 8, ["modelValue", "disabled"]),
                          (_unref(store).saveError)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_17, [...(_cache[19] || (_cache[19] = [
                                _createElementVNode("span", { class: "font-medium text-red-700" }, "The check was not saved.", -1),
                                _createTextVNode(" Your results and notes are still here. Try saving again. ", -1)
                              ]))]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_18, [
                            _createVNode(LlSecondaryButton, {
                              color: "primary",
                              loading: _unref(store).saving,
                              disabled: notesOverLimit.value,
                              "data-cy": "conflict-save-bottom",
                              onClick: saveCheck
                            }, {
                              default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                _createTextVNode(" Save check ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["loading", "disabled"])
                          ])
                        ]),
                        (_unref(store).allRows.length)
                          ? (_openBlock(), _createBlock(ConflictFieldFilter, {
                              key: 4,
                              class: "print:hidden",
                              "field-counts": _unref(store).fieldCounts,
                              "hidden-fields": _unref(store).hiddenFields,
                              "hidden-row-count": _unref(store).hiddenRowCount,
                              "permitted-fields": _unref(store).permittedFields,
                              "included-time-entries": _unref(store).ranWithTimeEntries,
                              onToggle: _unref(store).toggleField,
                              onShowAll: _unref(store).showAllFields
                            }, null, 8, ["field-counts", "hidden-fields", "hidden-row-count", "permitted-fields", "included-time-entries", "onToggle", "onShowAll"]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_19, [
                          _cache[21] || (_cache[21] = _createElementVNode("div", { class: "text-sm font-bold" }, "Notes", -1)),
                          (_unref(store).notes.trim())
                            ? (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(_unref(store).notes), 1))
                            : (_openBlock(), _createElementBlock("p", _hoisted_21, "No notes were recorded."))
                        ])
                      ], 64))
                    : _createCommentVNode("", true)
            ], 64))
    ]),
    (showPreview.value)
      ? (_openBlock(), _createBlock(ConflictPreviewPanel, {
          key: 0,
          class: "sticky top-0 max-h-screen self-start print:hidden",
          record: _unref(store).preview,
          loading: _unref(store).previewLoading,
          error: _unref(store).previewError,
          "included-time-entries": _unref(store).ranWithTimeEntries,
          "client-href": previewClientHref.value,
          "matter-href": previewMatterHref.value,
          onClose: _unref(store).closePreview
        }, null, 8, ["record", "loading", "error", "included-time-entries", "client-href", "matter-href", "onClose"]))
      : _createCommentVNode("", true),
    _createVNode(LlConfirmationDialog, {
      ref_key: "leaveDialog",
      ref: leaveDialog,
      title: "Leave without saving?",
      message: "Your notes on this check have not been saved. If you leave now they will be lost.",
      "confirm-text": "Leave",
      "toggle-text": "Stay",
      "data-cy": "conflict-leave-dialog"
    }, null, 512)
  ], 4))
}
}

})